import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createClient, AnamEvent } from '@anam-ai/js-sdk';
import {
  MESSAGE_TYPES,
  SIMULATION_TYPES,
  ANAM_VIDEO_ELEMENT_ID
} from '../../constants/simulationConstants';
import { useSimulationContext } from '../../providers/SimulationProvider';
import {
  isNudgytAgentCoreSession,
  isNudgytAgentSession,
  postMessage
} from '../../helpers/utils';
import ChromaKeyCanvas from './ChromaKeyCanvas';
import envConfig from '../../config/env.config';
import AnamConvaiClient from './AnamConvaiClient';
import anamConfig from '../../config/anamConfig';
import ChromaKeyControls from './ChromaKeyControls';
import AnamAvatarLoader from './AnamAvatarLoader';
import AnamBackgroundCanvas from './AnamBackgroundCanvas';
import AnamNudgytAgentClient from './AnamNudgytAgentClient';
import AnamNudgytAgentCoreClient from './AnamNudgytAgentCoreClient';
import useAnamAudioActivity from '../../hooks/useAnamAudioActivity';
import useAudioMessageListener from '../../hooks/useAudioMessageListener';
import useAnamVideoStreamRefresh from '../../hooks/useAnamVideoStreamRefresh';
import { cn } from '../../helpers/cn';

// Bytes per sample of the pcm_s16le stream handed to Anam
const PCM_16_BYTES = 2;
// How often a pending hide re-checks whether Anam has finished speaking
const HIDDEN_TEARDOWN_POLL_MS = 250;
// Gap tolerated between locally played chunks before the avatar is revealed. A
// long reply is generated more slowly than it is spoken, so its queue runs dry
// mid-reply — revealing on the first gap would flash the avatar and hide it
// again a moment later.
const LOCAL_AUDIO_SETTLE_MS = 1200;
// Safety net only: caps how long the avatar stays behind its loader waiting for
// locally played audio to finish, should that state ever get stuck. The session
// is connected and billing either way.
const LOCAL_AUDIO_REVEAL_MAX_DELAY_MS = 120000; // 2 minutes

function AnamVideoStream() {
  const [isAnamInitialized, setIsAnamInitialized] = useState(false);
  const [isLocalAudioSettled, setIsLocalAudioSettled] = useState(true);
  const [isLocalAudioOverdue, setIsLocalAudioOverdue] = useState(false);
  const [webGlSettings, setWebGlSettings] = useState({
    minGreen: 35,
    greenBias: 0.96,
    softness: 80,
    spill: 0.45,
    minSaturation: 0.08,
    spillDominance: 8,
    spillMinGreen: 70
  });

  const isActiveRef = useRef(false);
  const anamClientRef = useRef(null);
  const videoStreamRef = useRef(null);
  const lastSessionTokenRef = useRef(null);
  const connectingTokenRef = useRef(null);
  const audioInputStreamRef = useRef(null);
  const aiResponsePlaybackActiveRef = useRef(false); // Convai response start → Anam avatar speech end
  const pendingSpeechEndsAtRef = useRef(0); // Estimated time Anam finishes speaking what it was sent
  const isAudioSequenceCompleteRef = useRef(true); // All of the current reply's audio has been handed to Anam

  const {
    isSceneReady,
    simulationType,
    characterEngineType,
    setIsAiTalking,
    isUserInterrupt,
    isAvatarHidden,
    anamSessionToken,
    isLocalAudioPlaying,
    isClientInitialized,
    setIsClientInitialized,
    setIsUserInterrupt
  } = useSimulationContext();

  const isVideoStreamReady = isAnamInitialized && isSceneReady;
  const showChromaKeyControls =
    envConfig.nodeEnv === 'development' && isVideoStreamReady;

  /**
   * Local playback is only considered finished once the queue has stayed empty
   * for a moment — see LOCAL_AUDIO_SETTLE_MS.
   */
  useEffect(() => {
    if (isLocalAudioPlaying) {
      setIsLocalAudioSettled(false);
      return;
    }

    const timeoutId = setTimeout(
      () => setIsLocalAudioSettled(true),
      LOCAL_AUDIO_SETTLE_MS
    );

    return () => clearTimeout(timeoutId);
  }, [isLocalAudioPlaying]);

  /**
   * Safety net only: the loader must never outlive the audio it is waiting for.
   * If local playback ever fails to report that it finished, show the avatar
   * anyway — see LOCAL_AUDIO_REVEAL_MAX_DELAY_MS.
   */
  useEffect(() => {
    if (isAvatarHidden || isLocalAudioSettled) {
      setIsLocalAudioOverdue(false);
      return;
    }

    const timeoutId = setTimeout(
      () => setIsLocalAudioOverdue(true),
      LOCAL_AUDIO_REVEAL_MAX_DELAY_MS
    );

    return () => clearTimeout(timeoutId);
  }, [isAvatarHidden, isLocalAudioSettled]);

  const stopStream = useCallback(() => {
    isActiveRef.current = false;
    audioInputStreamRef.current = null;
    pendingSpeechEndsAtRef.current = 0;
    isAudioSequenceCompleteRef.current = true;

    if (anamClientRef.current) {
      anamClientRef.current.stopStreaming().catch((err) => {
        console.error('[Anam] stopStreaming error:', err);
      });
      setIsAnamInitialized(false);
      anamClientRef.current = null;
    }
  }, [audioInputStreamRef]);

  const startSession = useCallback(
    async (isReconnect = false) => {
      if (!anamSessionToken) {
        console.error('[Anam] No session token');
        return;
      }
      // Skip when already connected, or a connect for this token is in flight
      if (
        (lastSessionTokenRef.current === anamSessionToken &&
          isActiveRef.current) ||
        connectingTokenRef.current === anamSessionToken
      ) {
        return;
      }

      connectingTokenRef.current = anamSessionToken;
      stopStream();
      isActiveRef.current = true;

      // Tracked so late events from a client we already tore down (an
      // intentional stop, or a superseded reconnect) can be ignored below.
      let anamClient = null;

      try {
        anamClient = createClient(anamSessionToken, {
          disableInputAudio: true
        });
        anamClientRef.current = anamClient;

        anamClient.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
          if (!isActiveRef.current) return;
          console.log('[Anam] Connection established');
        });

        anamClient.addListener(AnamEvent.VIDEO_PLAY_STARTED, () => {
          if (!isActiveRef.current) return;
          setIsAnamInitialized(true);

          if (isReconnect)
            postMessage({ type: MESSAGE_TYPES.SIMULATION_RECONNECTED });

          console.log('[Anam] Video stream started');

          try {
            const inputStream = anamClient.createAgentAudioInputStream({
              encoding: anamConfig.audioEncoding,
              sampleRate: anamConfig.sampleRate,
              channels: anamConfig.channels
            });

            audioInputStreamRef.current = inputStream;
            lastSessionTokenRef.current = anamSessionToken;
            connectingTokenRef.current = null;
          } catch (err) {
            postMessage({ type: MESSAGE_TYPES.SIMULATION_ENDED });
            console.error('[Anam] Initialize Audio InputStream failed:', err);
          }
        });

        anamClient.addListener(
          AnamEvent.TALK_STREAM_INTERRUPTED,
          (correlationId) => {
            if (!isActiveRef.current) return;
            console.log('[Anam] Interrupted:', correlationId);
            setIsAiTalking(false);
          }
        );

        anamClient.addListener(
          AnamEvent.CONNECTION_CLOSED,
          (reason, details) => {
            // Ignore closes belonging to a client we already replaced or tore
            // down on purpose (avatar hidden, reconnect) — stopStream() clears
            // the ref synchronously, so only unexpected drops get past here.
            if (anamClientRef.current !== anamClient) return;

            console.error('[Anam]', reason, details);
            isActiveRef.current = false;
            connectingTokenRef.current = null;
            audioInputStreamRef.current = null;
            anamClientRef.current = null;
            setIsAnamInitialized(false);
            postMessage({ type: MESSAGE_TYPES.SIMULATION_PAUSED });
          }
        );

        anamClient.addListener(AnamEvent.SERVER_WARNING, (message) => {
          console.warn('[Anam] Server warning:', message);
        });

        await anamClient.streamToVideoElement(ANAM_VIDEO_ELEMENT_ID);
      } catch (error) {
        console.error('[Anam] Failed to start stream:', error);

        // A connect aborted by an intentional teardown (e.g. the avatar was
        // hidden mid-connect) is not a simulation failure — don't tell the
        // parent the session ended, it would disable the chat input.
        if (anamClient && anamClientRef.current !== anamClient) return;

        postMessage({ type: MESSAGE_TYPES.SIMULATION_ENDED });
        setIsClientInitialized(false);
        setIsAnamInitialized(false);
        isActiveRef.current = false;
        connectingTokenRef.current = null;
        audioInputStreamRef.current = null;
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [anamSessionToken]
  );

  const handleSendAudioChunk = useCallback((pcmU8) => {
    if (!audioInputStreamRef.current) return;
    audioInputStreamRef.current.sendAudioChunk(pcmU8);
    isAudioSequenceCompleteRef.current = false;

    /**
     * Track how far ahead of playback we are. Anam buffers everything it is
     * sent and speaks it in real time, and the SDK exposes no "finished
     * speaking" event, so this running estimate is the only way a deferred
     * teardown can know when the coach will actually be done. It cannot use
     * the audio-activity analyser: that runs on requestAnimationFrame, which
     * the browser suspends as soon as the parent hides this iframe.
     */
    const bytesPerSecond =
      Number(anamConfig.sampleRate) *
      Number(anamConfig.channels) *
      PCM_16_BYTES;
    const now = Date.now();

    if (pendingSpeechEndsAtRef.current < now)
      pendingSpeechEndsAtRef.current = now + anamConfig.audioStartLatencyMs;

    pendingSpeechEndsAtRef.current +=
      (pcmU8.byteLength / bytesPerSecond) * 1000;
  }, []);

  const handleEndAudioSequence = useCallback(() => {
    // Queued behind every chunk of the reply, so reaching here means Anam has
    // been given all of its audio — the signal a pending hide waits on, since
    // audio arrives more slowly than it is spoken and the buffer repeatedly
    // runs dry mid-reply.
    isAudioSequenceCompleteRef.current = true;

    if (!audioInputStreamRef.current) return;
    audioInputStreamRef.current.endSequence();
  }, []);

  const forceReconnect = useCallback(() => {
    lastSessionTokenRef.current = null;
    startSession(true);
  }, [startSession]);

  const renderClient = useCallback(() => {
    // Checked before BASIC: a Core character reports simulationType BASIC but
    // runs on the agent client, so the BASIC branch would claim it first.
    if (isNudgytAgentCoreSession({ characterEngineType }))
      return (
        <AnamNudgytAgentCoreClient
          aiResponsePlaybackActiveRef={aiResponsePlaybackActiveRef}
          isVideoStreamReady={isVideoStreamReady}
          handleSendAudioChunk={handleSendAudioChunk}
          handleEndAudioSequence={handleEndAudioSequence}
        />
      );
    if (isNudgytAgentSession({ simulationType, characterEngineType }))
      return (
        <AnamNudgytAgentClient
          aiResponsePlaybackActiveRef={aiResponsePlaybackActiveRef}
          isVideoStreamReady={isVideoStreamReady}
          handleSendAudioChunk={handleSendAudioChunk}
          handleEndAudioSequence={handleEndAudioSequence}
        />
      );
    if (simulationType === SIMULATION_TYPES.BASIC)
      return (
        <AnamConvaiClient
          aiResponsePlaybackActiveRef={aiResponsePlaybackActiveRef}
          isVideoStreamReady={isVideoStreamReady}
          handleSendAudioChunk={handleSendAudioChunk}
          handleEndAudioSequence={handleEndAudioSequence}
        />
      );
    return null;
  }, [
    isVideoStreamReady,
    simulationType,
    characterEngineType,
    aiResponsePlaybackActiveRef,
    handleSendAudioChunk,
    handleEndAudioSequence
  ]);

  const isAvatarTalking = useAnamAudioActivity(videoStreamRef);
  useAudioMessageListener({ audioRef: videoStreamRef });

  /**
   * A reply keeps the audio path it started on, so showing the avatar again
   * mid-reply leaves Anam connected with nothing to say while the rest of that
   * reply plays locally. Keep the box loading until the voice is Anam's again,
   * rather than revealing an avatar that stands there idle — but never hold the
   * loader over Anam's own speech, which supersedes whatever was playing
   * locally (see RESPONSE_START in useAnamNudgytAgentClient).
   */
  const isFinishingLocalAudio =
    !isAvatarHidden &&
    !isLocalAudioSettled &&
    !isLocalAudioOverdue &&
    !isAvatarTalking;
  const isAvatarRevealed = isVideoStreamReady && !isFinishingLocalAudio;

  useAnamVideoStreamRefresh({
    onRefresh: forceReconnect,
    aiResponsePlaybackActiveRef,
    isAvatarTalking,
    // Don't race the pending teardown with a refresh reconnect once the avatar
    // has been hidden — both wait for the same playback-idle moment.
    isActive: isAnamInitialized && !isAvatarHidden
  });

  useEffect(() => {
    // The analyser only speaks for Anam. It is frozen while hidden
    // (requestAnimationFrame does not run in a non-rendered iframe), and it is
    // silent while the tail of a reply finishes playing locally after the
    // avatar is shown again — local playback owns isAiTalking in both cases.
    if (!isAvatarHidden && !isLocalAudioPlaying) {
      if (isAvatarTalking) {
        setIsAiTalking(true);
      } else {
        setIsAiTalking(false);
        aiResponsePlaybackActiveRef.current = false;
      }
    }
    if (isUserInterrupt) {
      // No client while the avatar is hidden — local playback handles the
      // interrupt instead (see useAnamNudgytAgentClient).
      anamClientRef.current?.interruptPersona();
      audioInputStreamRef.current?.endSequence();
      // Buffered speech was just discarded, and flushQueue means the reply's
      // end-of-sequence may never arrive — nothing is pending anymore.
      pendingSpeechEndsAtRef.current = 0;
      isAudioSequenceCompleteRef.current = true;
      setIsAiTalking(false);
      aiResponsePlaybackActiveRef.current = false;
      setIsUserInterrupt(false);
    }
  }, [
    isAvatarTalking,
    isUserInterrupt,
    isAvatarHidden,
    isLocalAudioPlaying,
    setIsAiTalking,
    setIsUserInterrupt
  ]);

  /**
   * Anam session lifecycle:
   * 1. Initial connect — start immediately when the first session token arrives.
   * 2. Token refresh — defer reconnect until Convai→Anam playback is idle
   *    (aiResponsePlaybackActiveRef + isAvatarTalking) so speech is not cut off.
   * 3. Token cleared — tear down the stream (e.g. simulation reset).
   */
  useEffect(() => {
    if (!anamSessionToken) {
      stopStream();
      lastSessionTokenRef.current = null;
      connectingTokenRef.current = null;
      return;
    }

    // Never (re)connect while hidden — a refreshed token would otherwise start
    // a new billable session behind a hidden avatar. The token is still stored,
    // so showing the avatar again connects with the latest one.
    if (isAvatarHidden) return;

    const isConnectedToToken =
      lastSessionTokenRef.current === anamSessionToken && isActiveRef.current;
    if (isConnectedToToken) return;

    const hasNewToken = lastSessionTokenRef.current !== anamSessionToken;
    const isPlaybackIdle =
      !aiResponsePlaybackActiveRef.current && !isAvatarTalking;

    const isInitialConnect =
      lastSessionTokenRef.current === null && !isActiveRef.current;

    const shouldReconnectAfterPlayback =
      lastSessionTokenRef.current !== null && hasNewToken && isPlaybackIdle;

    if (isInitialConnect || shouldReconnectAfterPlayback) {
      startSession();
    }
  }, [
    anamSessionToken,
    isAvatarTalking,
    isAvatarHidden,
    startSession,
    stopStream
  ]);

  /**
   * Avatar shown again → reconnect. `isReconnect` makes VIDEO_PLAY_STARTED
   * post SIMULATION_RECONNECTED so the parent re-enables the avatar UI.
   */
  useEffect(() => {
    if (isAvatarHidden) return;
    if (anamSessionToken && !isActiveRef.current) startSession(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvatarHidden]);

  /**
   * Avatar hidden → stop the Anam session so it stops consuming Anam usage.
   *
   * Deferred until the coach has finished speaking. Audio is streamed into Anam
   * far ahead of playback, so stopping mid-reply would discard everything Anam
   * has buffered but not yet spoken, and that audio cannot be replayed locally.
   * Replies arriving after the teardown play locally instead (see
   * useAnamNudgytAgentClient).
   */
  useEffect(() => {
    if (!isAvatarHidden) return;

    const teardown = () => {
      stopStream();
      // Clear the in-flight guard so showing again may reuse the same token.
      connectingTokenRef.current = null;
      aiResponsePlaybackActiveRef.current = false;
      // The audio-activity analyser is frozen (no requestAnimationFrame in a
      // hidden iframe), so it can never clear this — do it explicitly or the
      // parent's "audio playing" indicator stays on forever.
      setIsAiTalking(false);
    };

    /**
     * Wait for the coach to finish, then tear down. Two conditions, because
     * neither alone is enough:
     *
     * - More audio may still be coming. A long reply is generated more slowly
     *   than it is spoken, so Anam's buffer runs dry between chunks; waiting
     *   only on the buffer tears down in one of those gaps.
     * - Anam may still be speaking what it already holds, after the last chunk
     *   has been handed over.
     *
     * Polled, since the estimate keeps extending while the reply streams in.
     * Deliberately not keyed on isAvatarTalking — that signal stops updating
     * the moment the parent hides this iframe.
     */
    const isSpeechPending = () =>
      !isAudioSequenceCompleteRef.current ||
      Date.now() < pendingSpeechEndsAtRef.current;

    if (!isSpeechPending()) {
      teardown();
      return;
    }

    const deadline = Date.now() + anamConfig.hiddenTeardownMaxDelayMs;
    const intervalId = setInterval(() => {
      if (isSpeechPending() && Date.now() < deadline) return;
      clearInterval(intervalId);
      teardown();
    }, HIDDEN_TEARDOWN_POLL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAvatarHidden]);

  return (
    <div
      className={cn(
        'relative w-full h-full overflow-hidden',
        isClientInitialized ? 'opacity-100' : 'opacity-0'
      )}
    >
      {renderClient()}

      <video
        ref={videoStreamRef}
        id={ANAM_VIDEO_ELEMENT_ID}
        autoPlay
        playsInline
        muted={false}
        className='w-full h-full object-cover opacity-0'
        aria-label='Anam AI avatar video stream'
      />

      {isClientInitialized && !isAvatarHidden && !isAvatarRevealed && (
        <AnamAvatarLoader
          message={
            isFinishingLocalAudio
              ? 'Coach will take over after this current audio has finished playing.'
              : 'Connecting coach…'
          }
        />
      )}

      <AnamBackgroundCanvas />
      <ChromaKeyCanvas
        videoElementId={ANAM_VIDEO_ELEMENT_ID}
        isStreaming={isAvatarRevealed}
        settings={webGlSettings}
      />

      {showChromaKeyControls && (
        <div className='absolute top-0 left-0 flex flex-col justify-center mt-4 ml-4'>
          <ChromaKeyControls
            settings={webGlSettings}
            setSettings={setWebGlSettings}
          />
        </div>
      )}
    </div>
  );
}

export default AnamVideoStream;
